import FingerprintJS from '@fingerprintjs/fingerprintjs';
import Cookies from 'js-cookie';
import { v4 as uuidv4 } from 'uuid';

const VISITOR_ID_COOKIE = 'footyai_visitor';
const COOKIE_EXPIRY = 365; // 1 year

let fpPromise: Promise<any> | null = null;
let visitorIdPromise: Promise<string> | null = null;
let cachedVisitorId: string | null = null;

export const getVisitorId = async (): Promise<string> => {
  // Return cached ID if available
  if (cachedVisitorId) {
    return cachedVisitorId;
  }

  // Return existing promise if we're already getting the visitor ID
  if (visitorIdPromise) {
    return visitorIdPromise;
  }

  // Create new promise for getting visitor ID
  visitorIdPromise = (async () => {
    try {
      // First check for existing cookie
      const existingId = Cookies.get(VISITOR_ID_COOKIE);
      if (existingId) {
        console.debug('Using existing visitor ID from cookie:', existingId);
        cachedVisitorId = existingId;
        return existingId;
      }

      // Generate a new visitor ID
      const newVisitorId = await generateVisitorId();
      
      // Store in cookie with 1 year expiry
      Cookies.set(VISITOR_ID_COOKIE, newVisitorId, { 
        expires: COOKIE_EXPIRY,
        sameSite: 'Strict',
        secure: window.location.protocol === 'https:'
      });

      cachedVisitorId = newVisitorId;
      return newVisitorId;
    } catch (error) {
      console.error('Error generating visitor ID:', error);
      // Fallback to UUID
      const fallbackId = uuidv4();
      Cookies.set(VISITOR_ID_COOKIE, fallbackId, { 
        expires: COOKIE_EXPIRY,
        sameSite: 'Strict',
        secure: window.location.protocol === 'https:'
      });
      cachedVisitorId = fallbackId;
      return fallbackId;
    } finally {
      // Clear the promise so we can try again if needed
      visitorIdPromise = null;
    }
  })();

  return visitorIdPromise;
};

async function generateVisitorId(): Promise<string> {
  try {
    // Initialize FingerprintJS if not already done
    if (!fpPromise) {
      fpPromise = FingerprintJS.load();
    }

    // Get visitor identifier
    const fp = await fpPromise;
    const result = await fp.get();

    console.debug('Generated fingerprint:', {
      visitorId: result.visitorId,
      confidence: result.confidence
    });

    return result.visitorId;
  } catch (error) {
    console.error('Failed to generate fingerprint:', error);
    throw error;
  }
}

// Add function to check if visitor ID exists
export const hasVisitorId = (): boolean => {
  return !!cachedVisitorId || !!Cookies.get(VISITOR_ID_COOKIE);
};

// Force visitor ID initialization
export const initializeVisitorId = async (): Promise<void> => {
  if (!hasVisitorId()) {
    await getVisitorId();
  }
};